import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { AssignmentDetail, AssignmentListItem } from '../../assignment/view';
import type {
  AssignmentDeliveryRuleDraftInput,
  AssignmentDraftInput,
  MemberGroupSummary,
  MemberSelectorListItem
} from '../appService';
import { formatDateKey, formatScheduleDateChip } from '../scheduleView';
import { useAppRuntime, useWorkspaceBootstrapContext, useWorkspaceContext, useWorkspaceRouteAdapter } from './context';
import {
  assignmentDetailQueryKey,
  assignmentListQueryKey,
  useAssignmentDetailQuery,
  useAssignmentListQuery,
  useMemberGroupDetailQuery,
  useMemberGroupSummaryListQuery,
  useMemberSelectorItemsQuery
} from './data';
import {
  ActionError,
  BackHeader,
  CompactEmptyRow,
  ConfirmedField,
  ModuleEmptyState,
  PendingButton,
  RecoverableReadError,
  SectionLoading,
  WorkspaceModuleTopBar,
  formatActionError,
  formatMemberGroupNames
} from './ui';

type AssignmentReviewerKind = 'member_group' | 'member_group_tag' | 'season_membership';

const ASSIGNMENT_DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function WorkspaceAssignmentsTab(): ReactNode {
  const appContext = useWorkspaceBootstrapContext();
  const routeAdapter = useWorkspaceRouteAdapter();
  const assignments = useAssignmentListQuery();
  const canCreateAssignment = appContext.currentPermissionKeys.includes('assignment_publish_manage');

  return (
    <>
      <WorkspaceModuleTopBar
        title="作业"
        action={
          canCreateAssignment
            ? {
                label: '新建作业',
                dataAction: 'open-assignment-create',
                icon: 'plus',
                onClick: routeAdapter.openAssignmentCreate
              }
            : undefined
        }
      />
      <AssignmentListContent assignments={assignments} />
    </>
  );
}

export function AssignmentCreatePage(): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();

  return (
    <div className="route-outlet">
      <BackHeader
        title="新建作业"
        action="back-to-assignments"
        label="返回作业"
        onBack={routeAdapter.backToAssignments}
      />
      <AssignmentCreateContent />
    </div>
  );
}

export function AssignmentDetailPage({ assignmentId }: { readonly assignmentId: string }): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();
  const detail = useAssignmentDetailQuery(assignmentId);

  return (
    <div className="route-outlet">
      <BackHeader
        title="作业详情"
        action="back-to-assignments"
        label="返回作业"
        onBack={routeAdapter.backToAssignments}
      />
      <AssignmentDetailContent assignmentId={assignmentId} detail={detail} />
    </div>
  );
}

export function AssignmentSubmissionPage({ assignmentId }: { readonly assignmentId: string }): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();
  const detail = useAssignmentDetailQuery(assignmentId);

  return (
    <div className="route-outlet">
      <BackHeader
        title="提交作业"
        action="back-to-assignment-detail"
        label="返回作业详情"
        onBack={() => routeAdapter.backToAssignmentDetail(assignmentId)}
      />
      <AssignmentSubmissionContent assignmentId={assignmentId} detail={detail} />
    </div>
  );
}

function AssignmentListContent({
  assignments
}: {
  readonly assignments: ReturnType<typeof useAssignmentListQuery>;
}): ReactNode {
  if (assignments.status === 'pending') {
    return <SectionLoading body="正在读取作业。" />;
  }

  if (assignments.status === 'error') {
    return (
      <RecoverableReadError
        error={assignments.error}
        retryAction="retry-assignment-list"
        onRetry={() => void assignments.refetch()}
      />
    );
  }

  if (assignments.data.length === 0) {
    return <ModuleEmptyState icon="music" body="暂无作业" />;
  }

  return <AssignmentRows assignments={assignments.data} />;
}

function AssignmentRows({ assignments }: { readonly assignments: readonly AssignmentListItem[] }): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();

  return (
    <section className="assignment-list prototype-list">
      {assignments.map((assignment) => {
        const chipText = formatScheduleDateChip(assignment.dueAt);
        return (
          <button
            type="button"
            className="assignment-row prototype-list-row"
            data-action="open-assignment-detail"
            data-assignment-id={assignment.assignmentId}
            aria-label={`打开作业${assignment.pieceName}`}
            key={assignment.assignmentId}
            onClick={() => routeAdapter.openAssignmentDetail(assignment.assignmentId)}
          >
            <span className="schedule-date-chip">
              <strong>{chipText.day}</strong>
              <small>{chipText.weekday}</small>
            </span>
            <span className="member-copy">
              <strong>{assignment.pieceName}</strong>
              <small>{formatAssignmentRowMeta(assignment)}</small>
            </span>
            <span className="choir-context-chevron" aria-hidden="true">
              ›
            </span>
          </button>
        );
      })}
    </section>
  );
}

function AssignmentCreateContent(): ReactNode {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();
  const routeAdapter = useWorkspaceRouteAdapter();
  const memberGroups = useMemberGroupSummaryListQuery();
  const memberSelector = useMemberSelectorItemsQuery('member_selection');
  const [pieceName, setPieceName] = useState('');
  const [dueDate, setDueDate] = useState(() => getDefaultAssignmentDueDateKey());
  const [dueTime, setDueTime] = useState('22:00');
  const [submissionType, setSubmissionType] = useState<AssignmentDraftInput['submissionType']>('audio');
  const [description, setDescription] = useState('');
  const [targetMemberGroupId, setTargetMemberGroupId] = useState('');
  const [reviewerKind, setReviewerKind] = useState<AssignmentReviewerKind>('member_group');
  const [reviewerMemberGroupId, setReviewerMemberGroupId] = useState('');
  const [reviewerTagGroupId, setReviewerTagGroupId] = useState('');
  const [reviewerTagId, setReviewerTagId] = useState('');
  const [reviewerSeasonMembershipId, setReviewerSeasonMembershipId] = useState('');
  const reviewerTagGroup = useMemberGroupDetailQuery(
    reviewerTagGroupId,
    reviewerKind === 'member_group_tag' && reviewerTagGroupId !== ''
  );
  const [actionError, setActionError] = useState<string | null>(null);
  const createAssignment = useMutation({
    mutationFn: (draft: AssignmentDraftInput) => service.createAssignment({ workspaceContext, draft }),
    onSuccess: async (createdDetail) => {
      queryClient.setQueryData(assignmentDetailQueryKey(workspaceContext, createdDetail.assignmentId), createdDetail);
      await queryClient.invalidateQueries({
        queryKey: assignmentListQueryKey(workspaceContext),
        exact: true
      });
      routeAdapter.replaceAssignmentDetail(createdDetail.assignmentId);
    }
  });

  useEffect(() => {
    if (memberGroups.status !== 'success' || memberGroups.data.length === 0) {
      return;
    }
    const firstGroupId = memberGroups.data[0].memberGroupId;
    const hasGroup = (groupId: string) =>
      memberGroups.data.some((memberGroup) => memberGroup.memberGroupId === groupId);
    if (!hasGroup(targetMemberGroupId)) {
      setTargetMemberGroupId(firstGroupId);
    }
    if (!hasGroup(reviewerMemberGroupId)) {
      setReviewerMemberGroupId(firstGroupId);
    }
    if (!hasGroup(reviewerTagGroupId)) {
      setReviewerTagGroupId(firstGroupId);
    }
  }, [memberGroups.status, memberGroups.data, reviewerMemberGroupId, reviewerTagGroupId, targetMemberGroupId]);

  useEffect(() => {
    if (memberSelector.status !== 'success' || memberSelector.data.length === 0) {
      return;
    }
    const hasMember = memberSelector.data.some((member) => member.seasonMembershipId === reviewerSeasonMembershipId);
    if (!hasMember) {
      setReviewerSeasonMembershipId(memberSelector.data[0].seasonMembershipId);
    }
  }, [memberSelector.status, memberSelector.data, reviewerSeasonMembershipId]);

  useEffect(() => {
    if (reviewerTagGroup.status !== 'success' || reviewerTagGroup.data.tags.length === 0) {
      return;
    }
    const hasTag = reviewerTagGroup.data.tags.some((tag) => tag.memberGroupTagId === reviewerTagId);
    if (!hasTag) {
      setReviewerTagId(reviewerTagGroup.data.tags[0].memberGroupTagId);
    }
  }, [reviewerTagGroup.status, reviewerTagGroup.data, reviewerTagId]);

  if (memberGroups.status === 'pending' || memberSelector.status === 'pending') {
    return <SectionLoading body="正在读取作业创建选项。" />;
  }

  if (memberGroups.status === 'error') {
    return (
      <RecoverableReadError
        error={memberGroups.error}
        retryAction="retry-assignment-create-options"
        onRetry={() => {
          void memberGroups.refetch();
          void memberSelector.refetch();
        }}
      />
    );
  }

  if (memberSelector.status === 'error') {
    return (
      <RecoverableReadError
        error={memberSelector.error}
        retryAction="retry-assignment-create-options"
        onRetry={() => {
          void memberGroups.refetch();
          void memberSelector.refetch();
        }}
      />
    );
  }

  const canSubmit =
    memberGroups.data.length > 0 &&
    (reviewerKind !== 'season_membership' || memberSelector.data.length > 0) &&
    (reviewerKind !== 'member_group_tag' ||
      (reviewerTagGroup.status === 'success' && reviewerTagGroup.data.tags.length > 0));

  const submit = (): void => {
    void (async () => {
      setActionError(null);
      try {
        await createAssignment.mutateAsync(
          buildAssignmentCreateDraft({
            pieceName,
            dueDate,
            dueTime,
            submissionType,
            description,
            targetMemberGroupId,
            reviewerKind,
            reviewerMemberGroupId,
            reviewerTagId,
            reviewerSeasonMembershipId
          })
        );
      } catch (error) {
        setActionError(formatActionError(error));
      }
    })();
  };

  return (
    <section className="assignment-create-form schedule-form" data-screen="assignment-create">
      <ActionError message={actionError} />
      <section className="schedule-editor-panel" aria-label="作业信息">
        <label>
          <span>曲目</span>
          <input
            data-field="assignment-piece-name"
            value={pieceName}
            onChange={(event) => setPieceName(event.currentTarget.value)}
          />
        </label>
        <label>
          <span>截止日期</span>
          <input
            type="date"
            data-field="assignment-due-date"
            value={dueDate}
            onChange={(event) => setDueDate(event.currentTarget.value)}
          />
        </label>
        <label>
          <span>截止时间</span>
          <input
            type="time"
            data-field="assignment-due-time"
            value={dueTime}
            onChange={(event) => setDueTime(event.currentTarget.value)}
          />
        </label>
        <label>
          <span>提交类型</span>
          <select
            data-field="assignment-submission-type"
            value={submissionType}
            onChange={(event) => setSubmissionType(event.currentTarget.value as AssignmentDraftInput['submissionType'])}
          >
            <option value="audio">录音</option>
            <option value="video">视频</option>
          </select>
        </label>
        <label>
          <span>说明（可选）</span>
          <textarea
            data-field="assignment-description"
            value={description}
            onChange={(event) => setDescription(event.currentTarget.value)}
          />
        </label>
      </section>
      <AssignmentDeliveryRuleEditor
        memberGroups={memberGroups.data}
        members={memberSelector.data}
        reviewerTagGroup={reviewerTagGroup}
        targetMemberGroupId={targetMemberGroupId}
        reviewerKind={reviewerKind}
        reviewerMemberGroupId={reviewerMemberGroupId}
        reviewerTagGroupId={reviewerTagGroupId}
        reviewerTagId={reviewerTagId}
        reviewerSeasonMembershipId={reviewerSeasonMembershipId}
        onTargetMemberGroupChange={setTargetMemberGroupId}
        onReviewerKindChange={setReviewerKind}
        onReviewerMemberGroupChange={setReviewerMemberGroupId}
        onReviewerTagGroupChange={setReviewerTagGroupId}
        onReviewerTagChange={setReviewerTagId}
        onReviewerSeasonMembershipChange={setReviewerSeasonMembershipId}
      />
      <PendingButton
        action="create-assignment"
        label="创建作业"
        variant="primary"
        pending={createAssignment.isPending}
        onClick={submit}
      />
      {!canSubmit ? <CompactEmptyRow body="请先补齐交付关系选项" /> : null}
    </section>
  );
}

function AssignmentDeliveryRuleEditor({
  memberGroups,
  members,
  reviewerTagGroup,
  targetMemberGroupId,
  reviewerKind,
  reviewerMemberGroupId,
  reviewerTagGroupId,
  reviewerTagId,
  reviewerSeasonMembershipId,
  onTargetMemberGroupChange,
  onReviewerKindChange,
  onReviewerMemberGroupChange,
  onReviewerTagGroupChange,
  onReviewerTagChange,
  onReviewerSeasonMembershipChange
}: {
  readonly memberGroups: readonly MemberGroupSummary[];
  readonly members: readonly MemberSelectorListItem[];
  readonly reviewerTagGroup: ReturnType<typeof useMemberGroupDetailQuery>;
  readonly targetMemberGroupId: string;
  readonly reviewerKind: AssignmentReviewerKind;
  readonly reviewerMemberGroupId: string;
  readonly reviewerTagGroupId: string;
  readonly reviewerTagId: string;
  readonly reviewerSeasonMembershipId: string;
  readonly onTargetMemberGroupChange: (value: string) => void;
  readonly onReviewerKindChange: (value: AssignmentReviewerKind) => void;
  readonly onReviewerMemberGroupChange: (value: string) => void;
  readonly onReviewerTagGroupChange: (value: string) => void;
  readonly onReviewerTagChange: (value: string) => void;
  readonly onReviewerSeasonMembershipChange: (value: string) => void;
}): ReactNode {
  return (
    <section className="schedule-editor-panel assignment-delivery-editor" aria-label="交付关系">
      <h2>交付关系</h2>
      <label className="field">
        <span>谁交付</span>
        <select
          data-field="assignment-delivery-target-group"
          value={targetMemberGroupId}
          onChange={(event) => onTargetMemberGroupChange(event.currentTarget.value)}
        >
          {memberGroups.map((memberGroup) => (
            <option key={memberGroup.memberGroupId} value={memberGroup.memberGroupId}>
              {memberGroup.name}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>谁评审</span>
        <select
          data-field="assignment-reviewer-kind"
          value={reviewerKind}
          onChange={(event) => onReviewerKindChange(event.currentTarget.value as AssignmentReviewerKind)}
        >
          <option value="member_group">成员组</option>
          <option value="member_group_tag">成员组标签</option>
          <option value="season_membership">个别成员</option>
        </select>
      </label>
      {reviewerKind === 'member_group' ? (
        <label className="field">
          <span>评审成员组</span>
          <select
            data-field="assignment-reviewer-group"
            value={reviewerMemberGroupId}
            onChange={(event) => onReviewerMemberGroupChange(event.currentTarget.value)}
          >
            {memberGroups.map((memberGroup) => (
              <option key={memberGroup.memberGroupId} value={memberGroup.memberGroupId}>
                {memberGroup.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {reviewerKind === 'member_group_tag' ? (
        <>
          <label className="field">
            <span>标签所属成员组</span>
            <select
              data-field="assignment-reviewer-tag-group"
              value={reviewerTagGroupId}
              onChange={(event) => onReviewerTagGroupChange(event.currentTarget.value)}
            >
              {memberGroups.map((memberGroup) => (
                <option key={memberGroup.memberGroupId} value={memberGroup.memberGroupId}>
                  {memberGroup.name}
                </option>
              ))}
            </select>
          </label>
          {reviewerTagGroup.status === 'pending' ? <SectionLoading body="正在读取标签。" /> : null}
          {reviewerTagGroup.status === 'success' ? (
            <label className="field">
              <span>评审标签</span>
              <select
                data-field="assignment-reviewer-tag"
                value={reviewerTagId}
                onChange={(event) => onReviewerTagChange(event.currentTarget.value)}
              >
                {reviewerTagGroup.data.tags.map((tag) => (
                  <option key={tag.memberGroupTagId} value={tag.memberGroupTagId}>
                    {tag.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </>
      ) : null}
      {reviewerKind === 'season_membership' ? (
        <label className="field">
          <span>评审成员</span>
          <select
            data-field="assignment-reviewer-member"
            value={reviewerSeasonMembershipId}
            onChange={(event) => onReviewerSeasonMembershipChange(event.currentTarget.value)}
          >
            {members.map((member) => (
              <option key={member.seasonMembershipId} value={member.seasonMembershipId}>
                {member.nickname}
              </option>
            ))}
          </select>
        </label>
      ) : null}
    </section>
  );
}

function AssignmentDetailContent({
  assignmentId,
  detail
}: {
  readonly assignmentId: string;
  readonly detail: ReturnType<typeof useAssignmentDetailQuery>;
}): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();

  if (detail.status === 'pending') {
    return <SectionLoading body="正在读取作业详情。" />;
  }

  if (detail.status === 'error') {
    return (
      <RecoverableReadError
        error={detail.error}
        retryAction="retry-assignment-detail"
        onRetry={() => void detail.refetch()}
      />
    );
  }

  return (
    <>
      <section className="schedule-detail-section assignment-detail-section">
        <h2>{detail.data.pieceName}</h2>
        <p>{`截止 ${formatAssignmentDateTime(detail.data.dueAt)}`}</p>
        <ConfirmedField label="提交类型" value={formatAssignmentSubmissionType(detail.data.submissionType)} />
        <ConfirmedField label="我的状态" value={formatAssignmentMySubmissionStatus(detail.data.mySubmissionStatus)} />
      </section>
      <section className="schedule-detail-section assignment-detail-section">
        <h2>说明</h2>
        <p>{detail.data.description || '暂无说明'}</p>
      </section>
      <section className="schedule-detail-section assignment-detail-section">
        <h2>我的提交</h2>
        {detail.data.mySubmission ? (
          <>
            <ConfirmedField label="状态" value="已提交" />
            <ConfirmedField label="提交时间" value={formatAssignmentDateTime(detail.data.mySubmission.submittedAt)} />
            <ConfirmedField label="文件名" value={detail.data.mySubmission.fileName} />
          </>
        ) : (
          <ConfirmedField label="状态" value={formatAssignmentMySubmissionStatus(detail.data.mySubmissionStatus)} />
        )}
        {detail.data.canSubmit ? (
          <button
            type="button"
            className="primary-button"
            data-action="open-assignment-upload"
            onClick={() => routeAdapter.openAssignmentSubmission(assignmentId)}
          >
            {formatAssignmentSubmitButtonLabel(detail.data)}
          </button>
        ) : null}
      </section>
      <AssignmentDeliveryRules detail={detail.data} />
      <AssignmentSubmissionRows detail={detail.data} />
    </>
  );
}

function AssignmentDeliveryRules({ detail }: { readonly detail: AssignmentDetail }): ReactNode {
  return (
    <section className="schedule-detail-section assignment-detail-section">
      <h2>交付关系</h2>
      {detail.deliveryRules.length === 0 ? (
        <CompactEmptyRow body="暂无交付关系" />
      ) : (
        <section className="assignment-delivery-list prototype-list">
          {detail.deliveryRules.map((deliveryRule) => (
            <article
              className="assignment-delivery-row prototype-list-row"
              data-assignment-delivery-rule-id={deliveryRule.assignmentDeliveryRuleId}
              key={deliveryRule.assignmentDeliveryRuleId}
            >
              <strong>{`${deliveryRule.targetSummary} -> ${deliveryRule.reviewerSummary}`}</strong>
            </article>
          ))}
        </section>
      )}
    </section>
  );
}

function AssignmentSubmissionRows({ detail }: { readonly detail: AssignmentDetail }): ReactNode {
  return (
    <section className="schedule-detail-section assignment-detail-section">
      <h2>公开提交状态</h2>
      {detail.submissionRows.length === 0 ? (
        <CompactEmptyRow body="暂无提交状态" />
      ) : (
        <section className="assignment-submission-list prototype-list" role="list">
          {detail.submissionRows.map((submissionRow) => (
            <article
              className="assignment-submission-row prototype-list-row"
              data-season-membership-id={submissionRow.seasonMembershipId}
              role="listitem"
              key={submissionRow.seasonMembershipId}
            >
              <span className="member-avatar">{submissionRow.nickname.trim().slice(0, 1) || '成'}</span>
              <span className="member-copy">
                <strong>{submissionRow.nickname}</strong>
                <small>{formatMemberGroupNames(submissionRow.memberGroupNames)}</small>
              </span>
              <span className="assignment-submission-status">
                {formatAssignmentPublicSubmissionStatus(submissionRow.status)}
              </span>
              <span className="assignment-submission-time">
                {formatAssignmentSubmissionTime(submissionRow.submittedAt)}
              </span>
            </article>
          ))}
        </section>
      )}
    </section>
  );
}

function AssignmentSubmissionContent({
  assignmentId,
  detail
}: {
  readonly assignmentId: string;
  readonly detail: ReturnType<typeof useAssignmentDetailQuery>;
}): ReactNode {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();
  const routeAdapter = useWorkspaceRouteAdapter();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [note, setNote] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionWarning, setActionWarning] = useState<string | null>(null);
  const submitAssignment = useMutation({
    mutationFn: async () => {
      const file = fileInputRef.current?.files?.[0] ?? null;
      if (!file) {
        throw new Error('Assignment submission file is required');
      }
      const extension = getUploadFileExtension(file.name);

      const intent = await service.createUploadIntent({
        purpose: 'assignment_submission',
        assignmentId,
        fileName: file.name,
        mimeType: file.type,
        extension,
        sizeBytes: file.size
      });
      await service.uploadAssignmentFile({ upload: intent.upload, file });
      await service.completeUpload({
        fileUploadId: intent.fileUploadId,
        objectKey: intent.objectKey,
        sizeBytes: file.size,
        etag: null
      });
      const trimmedNote = note.trim();
      return await service.submitMyAssignment({
        assignmentId,
        fileUploadId: intent.fileUploadId,
        note: trimmedNote === '' ? undefined : trimmedNote
      });
    },
    onSuccess: async (result) => {
      const listKey = assignmentListQueryKey(workspaceContext);
      const detailKey = assignmentDetailQueryKey(workspaceContext, assignmentId);
      await queryClient.invalidateQueries({ queryKey: listKey, exact: true });
      await queryClient.invalidateQueries({ queryKey: detailKey, exact: true });
      if (result.cleanupWarning) {
        setActionWarning(`提交已保存，但旧文件清理失败：${result.cleanupWarning}`);
        return;
      }
      routeAdapter.backToAssignmentDetail(assignmentId);
    }
  });

  if (detail.status === 'pending') {
    return <SectionLoading body="正在读取作业详情。" />;
  }

  if (detail.status === 'error') {
    return (
      <RecoverableReadError
        error={detail.error}
        retryAction="retry-assignment-upload"
        onRetry={() => void detail.refetch()}
      />
    );
  }

  if (!detail.data.canSubmit) {
    return (
      <section className="schedule-detail-section assignment-detail-section">
        <h2>已截止</h2>
        <p>作业已截止，不能再提交。</p>
      </section>
    );
  }

  return (
    <>
      {detail.data.canOverwrite || detail.data.mySubmission ? (
        <section className="assignment-overwrite-note">
          <p>提交成功后只保留最新文件，旧文件会被替换。</p>
        </section>
      ) : null}
      <section className="schedule-editor-panel assignment-upload-panel">
        <label>
          <span>文件</span>
          <input
            ref={fileInputRef}
            type="file"
            required
            accept={getAssignmentFileAccept(detail.data.submissionType)}
            data-field="assignment-submission-file"
          />
        </label>
        <label>
          <span>备注</span>
          <textarea
            data-field="assignment-submission-note"
            value={note}
            onChange={(event) => setNote(event.currentTarget.value)}
          />
        </label>
      </section>
      <ActionError message={actionError} />
      <ActionWarning message={actionWarning} />
      {detail.data.canSubmit ? (
        <div className="footer-actions">
          <PendingButton
            action="submit-assignment"
            label="提交"
            variant="primary"
            pending={submitAssignment.isPending}
            onClick={() => {
              void (async () => {
                setActionError(null);
                setActionWarning(null);
                try {
                  await submitAssignment.mutateAsync();
                } catch (error) {
                  setActionError(formatActionError(error));
                }
              })();
            }}
          />
        </div>
      ) : null}
    </>
  );
}

function ActionWarning({ message }: { readonly message: string | null }): ReactNode {
  const warningRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (message) {
      warningRef.current?.focus();
    }
  }, [message]);

  if (!message) {
    return null;
  }

  return (
    <div className="account-notice" role="alert" tabIndex={-1} ref={warningRef}>
      <p>{message}</p>
    </div>
  );
}

function getUploadFileExtension(fileName: string): string {
  const trimmedName = fileName.trim();
  const dotIndex = trimmedName.lastIndexOf('.');
  const extension =
    dotIndex === -1 || dotIndex === trimmedName.length - 1 ? '' : trimmedName.slice(dotIndex + 1).toLowerCase();
  if (extension === '') {
    throw new Error('Assignment submission file extension is required');
  }
  return extension;
}

function formatAssignmentRowMeta(assignment: AssignmentListItem): string {
  return [
    `截止 ${formatAssignmentDateTime(assignment.dueAt)}`,
    formatAssignmentSubmissionType(assignment.submissionType),
    formatAssignmentMySubmissionStatus(assignment.mySubmissionStatus),
    `${assignment.submittedCount}/${assignment.expectedSubmitterCount}`,
    `${assignment.deliveryRuleCount} 条交付关系`
  ].join(' · ');
}

function formatAssignmentSubmissionType(type: AssignmentDetail['submissionType']): string {
  return type === 'audio' ? '录音' : '视频';
}

function getAssignmentFileAccept(type: AssignmentDetail['submissionType']): string {
  return type === 'audio' ? 'audio/*' : 'video/*';
}

function formatAssignmentMySubmissionStatus(status: AssignmentListItem['mySubmissionStatus']): string {
  const labels: Record<AssignmentListItem['mySubmissionStatus'], string> = {
    not_submitted: '未提交',
    submitted: '已提交',
    ended: '已截止'
  };
  return labels[status];
}

function formatAssignmentPublicSubmissionStatus(status: AssignmentDetail['submissionRows'][number]['status']): string {
  return status === 'submitted' ? '已提交' : '未提交';
}

function formatAssignmentSubmitButtonLabel(detail: AssignmentDetail): string {
  const typeLabel = formatAssignmentSubmissionType(detail.submissionType);
  return detail.mySubmission ? `重新提交${typeLabel}` : `提交${typeLabel}`;
}

function formatAssignmentSubmissionTime(submittedAt: string | null): string {
  return submittedAt ? formatAssignmentDateTime(submittedAt) : '未提交';
}

function formatAssignmentDateTime(isoTimestamp: string): string {
  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) {
    throw new Error(`Invalid assignment timestamp: ${isoTimestamp}`);
  }
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hour = String(date.getUTCHours()).padStart(2, '0');
  const minute = String(date.getUTCMinutes()).padStart(2, '0');
  return `${year}-${month}-${day} ${hour}:${minute}`;
}

function getDefaultAssignmentDueDateKey(): string {
  return formatDateKey(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000));
}

function buildAssignmentCreateDraft(input: {
  readonly pieceName: string;
  readonly dueDate: string;
  readonly dueTime: string;
  readonly submissionType: AssignmentDraftInput['submissionType'];
  readonly description: string;
  readonly targetMemberGroupId: string;
  readonly reviewerKind: AssignmentReviewerKind;
  readonly reviewerMemberGroupId: string;
  readonly reviewerTagId: string;
  readonly reviewerSeasonMembershipId: string;
}): AssignmentDraftInput {
  const pieceName = input.pieceName.trim();
  if (!pieceName) {
    throw new Error('Assignment piece name is required');
  }
  if (!input.targetMemberGroupId) {
    throw new Error('Assignment delivery target member group is required');
  }
  if (!isAssignmentDueDateKey(input.dueDate) || !/^\d{2}:\d{2}$/.test(input.dueTime)) {
    throw new Error('Assignment due time must be valid');
  }

  const description = input.description.trim();
  return {
    pieceName,
    dueAt: toAssignmentDueAt(input.dueDate, input.dueTime),
    submissionType: input.submissionType,
    ...(description ? { description } : {}),
    deliveryRules: [
      {
        target: {
          memberGroupIds: [input.targetMemberGroupId],
          includeSeasonMembershipIds: [],
          excludeSeasonMembershipIds: []
        },
        reviewers: buildAssignmentReviewers(input)
      }
    ]
  };
}

function buildAssignmentReviewers(input: {
  readonly reviewerKind: AssignmentReviewerKind;
  readonly reviewerMemberGroupId: string;
  readonly reviewerTagId: string;
  readonly reviewerSeasonMembershipId: string;
}): AssignmentDeliveryRuleDraftInput['reviewers'] {
  if (input.reviewerKind === 'member_group') {
    if (!input.reviewerMemberGroupId) {
      throw new Error('Assignment reviewer is required');
    }
    return {
      memberGroupIds: [input.reviewerMemberGroupId],
      memberGroupTagIds: [],
      seasonMembershipIds: []
    };
  }

  if (input.reviewerKind === 'member_group_tag') {
    if (!input.reviewerTagId) {
      throw new Error('Assignment reviewer is required');
    }
    return {
      memberGroupIds: [],
      memberGroupTagIds: [input.reviewerTagId],
      seasonMembershipIds: []
    };
  }

  if (!input.reviewerSeasonMembershipId) {
    throw new Error('Assignment reviewer is required');
  }
  return {
    memberGroupIds: [],
    memberGroupTagIds: [],
    seasonMembershipIds: [input.reviewerSeasonMembershipId]
  };
}

function toAssignmentDueAt(dateKey: string, time: string): string {
  if (!isAssignmentDueDateKey(dateKey) || !/^\d{2}:\d{2}$/.test(time)) {
    throw new Error('Assignment due time must be valid');
  }

  return `${dateKey}T${time}:00.000Z`;
}

function isAssignmentDueDateKey(value: unknown): value is string {
  if (typeof value !== 'string' || !ASSIGNMENT_DATE_KEY_PATTERN.test(value)) {
    return false;
  }

  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() + 1 === month &&
    date.getUTCDate() === day &&
    formatDateKey(date) === value
  );
}
